import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Container from './Container';
import CtaLink from './CtaLink';

const ServicesCoreLogistics = () => {
    const [loadedById, setLoadedById] = useState({});
    const containerRef = useRef(null);
    const leftRef = useRef(null);
    const rightRef = useRef(null);

    const services = useMemo(() => [
        {
            id: 0,
            title: "Freight Forwarding",
            description: "Move your cargo seamlessly across India with our integrated Freight Forwarding solutions. From air and road to rail and express logistics, we deliver speed, reliability, and complete visibility at every stage. Whether it's urgent, high-value, or bulk shipments, our optimized multimodal network ensures your goods reach their destination safely and on time.",
            image: "/images/fright_forwarding.webp",
            imageAlt: "Container ship at sea",
            accordions: [
                {
                    id: 'air',
                    label: 'Air Freight',
                    content: 'Priority air cargo for critical, time-sensitive shipments with full tracking.'
                },
                {
                    id: 'road',
                    label: 'Road Freight',
                    content: 'PTL and FTL services with GPS-enabled vehicles and optimized routing.'
                },
                {
                    id: 'rail',
                    label: 'Rail Freight',
                    content: 'Cost-efficient long-haul movement with reliable schedules and visibility.'
                },
                {
                    id: 'express',
                    label: 'Express Freight / Same-Day Delivery',
                    content: 'Expedited delivery options for urgent business needs with assured timelines.'
                }
            ]
        },
        {
            id: 1,
            title: "Warehousing & Distribution",
            description: "Secure storage facilities with efficient inventory management and distribution tailored to your supply chain needs. Our state-of-the-art warehouses ensure your goods are stored safely and distributed efficiently across our network.",
            image: "/images/warehousing_distribution.webp",
            imageAlt: "Warehouse operations",
            accordions: [
                { id: 'storage', label: 'Storage Solutions', content: 'Ambient and temperature-controlled facilities with WMS integration.' },
                { id: 'inventory', label: 'Inventory Management', content: 'Cycle counts, SKU tracking, and real-time stock visibility.' }
            ]
        },
        {
            id: 2,
            title: "Customs Clearance",
            description: "Expert handling of documentation and compliance to ensure hassle-free cross-border shipping and faster clearance. Our experienced team navigates complex customs procedures to get your shipments moving quickly.",
            image: "/images/customs_clearance.webp",
            imageAlt: "Customs clearance process",
            accordions: [
                { id: 'docs', label: 'Documentation', content: 'End-to-end paperwork and compliance management for imports and exports.' },
                { id: 'lic', label: 'Licenses & Compliance', content: 'HS classification, duty drawbacks, and regulatory approvals.' }
            ]
        },
        {
            id: 3,
            title: "Last-Mile Delivery",
            description: "Timely and reliable delivery solutions that connect your shipments directly to the end customer with precision. Our last-mile network ensures your packages reach their final destination on time.",
            image: "/images/last_mile_delivery.webp",
            imageAlt: "Last mile delivery truck",
            accordions: [
                { id: 'cod', label: 'COD & Reverse Logistics', content: 'Cash on delivery handling and streamlined returns processing.' },
                { id: 'slot', label: 'Slot-Based Deliveries', content: 'Time-window deliveries for premium customer experience.' }
            ]
        },
        {
            id: 4,
            title: "Express Cargo Services",
            description: "Fast and flexible air and road cargo services designed to meet urgent business demands with reliability. When time is critical, our express services deliver your cargo with speed and precision.",
            image: "/images/express_cargo_services.webp",
            imageAlt: "Express cargo services",
            accordions: [
                { id: 'same', label: 'Same-Day', content: 'Door-to-door, same-day options across select corridors.' },
                { id: 'next', label: 'Next-Day', content: 'Guaranteed next-day deliveries for critical shipments.' }
            ]
        },
        {
            id: 5,
            title: "Supply Chain Solutions",
            description: "End-to-end logistics strategies that optimize operations, reduce costs, and drive long-term efficiency. Our comprehensive supply chain solutions help streamline your entire logistics process.",
            image: "/images/supply_chains_solution.webp",
            imageAlt: "Supply chain management",
            accordions: [
                { id: 'design', label: 'Network Design', content: 'Footprint optimization and route-to-market planning.' },
                { id: 'analytics', label: 'Analytics & Visibility', content: 'Control tower dashboards and predictive insights.' }
            ]
        },
    ], []);

    const [openByService, setOpenByService] = useState({});

    const toggleAccordion = (serviceId, accId) => {
        setOpenByService((prev) => {
            const current = prev[serviceId];
            return {
                ...prev,
                [serviceId]: current === accId ? null : accId,
            };
        });
    };

    useEffect(() => {
        services.forEach((svc) => {
            const img = new Image();
            img.src = svc.image;
            img.onload = () => {
                setLoadedById((prev) => ({ ...prev, [svc.id]: true }));
            };
            img.onerror = () => {
                setLoadedById((prev) => ({ ...prev, [svc.id]: true }));
            };
        });
    }, [services]);

    useLayoutEffect(() => {
        gsap.registerPlugin(ScrollTrigger);

        const ctx = gsap.context(() => {
            if (!containerRef.current || !leftRef.current || !rightRef.current) return;

            // Pin the left column for the duration of scrolling the right column
            ScrollTrigger.create({
                trigger: containerRef.current,
                start: 'top top',
                end: () => {
                    const rightHeight = rightRef.current.scrollHeight;
                    const viewport = window.innerHeight;
                    const extra = Math.max(0, rightHeight - viewport);
                    return '+=' + extra;
                },
                pin: leftRef.current,
                pinSpacing: false,
                scrub: true,
            });

            // Reveal each service card as it scrolls into view
            const items = gsap.utils.toArray('.service-showcase-item');
            items.forEach((item) => {
                gsap.from(item, {
                    opacity: 0,
                    y: 24,
                    duration: 0.6,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: item,
                        start: 'top 85%',
                        toggleActions: 'play none none reverse',
                    },
                });
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section className="section services-core-logistics bg-[#FFF6F6]" ref={containerRef}>
            <Container>
                <div className="core-logistics-sticky-container">
                    {/* Sticky Left Column */}
                    <div className="core-logistics-left sticky-left !bg-[#FFF6F6]" ref={leftRef}>
                        <div className="container section-badge flex items-center gap-3 text-[16px] md:text-[20px] !px-1">
                            <div className="badge-dot"></div>
                            Our Services
                        </div>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl">Core Logistic Services</h2>
                        <div className="cta-container">
                            <CtaLink href="/contact" primaryText="Get In Touch" className="text-black" fill="black" />
                        </div>
                    </div>

                    {/* Scrollable Right Column */}
                    <div className="core-logistics-right scrollable-right !bg-[#FFF6F6]" ref={rightRef}>
                        {services.map((service, index) => (
                            <div key={service.id} className="service-showcase-item">
                                <div className="service-number">{String(index + 1).padStart(2, "0")}</div>
                                <h3 className="text-xl md:text-2xl lg:text-3xl">{service.title}</h3>
                                <div className="service-image">
                                    <img
                                        src={service.image}
                                        alt={service.imageAlt}
                                        className={`transition-opacity duration-500 ${loadedById[service.id] ? "is-loaded" : "opacity-0"}`}
                                    />
                                </div>
                                <p className="service-description text-sm md:text-base lg:text-lg">
                                    {service.description}
                                </p>
                                {service.accordions && service.accordions.length > 0 && (
                                    <div className="accordion">
                                        {service.accordions.map((acc) => {
                                            const isOpen = openByService[service.id] === acc.id;
                                            return (
                                                <div key={acc.id} className={`accordion-item ${isOpen ? 'is-open' : ''}`}>
                                                    <button
                                                        type="button"
                                                        className="accordion-header"
                                                        onClick={() => toggleAccordion(service.id, acc.id)}
                                                        aria-expanded={isOpen}
                                                        aria-controls={`acc-content-${service.id}-${acc.id}`}
                                                    >
                                                        <span className="acc-bullet" aria-hidden="true"></span>
                                                        <span className="acc-label text-sm md:text-base">{acc.label}</span>
                                                        <span className={`acc-toggle ${isOpen ? 'rot-180' : ''}`} aria-hidden="true">
                                                            <svg width="24" height="24" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="17" cy="17" r="16.5" stroke="#ED1B24" />
                                                                <path d="M11.225 13L16.125 17.7533L17 18.5748L17.875 17.7533L22.775 13L24 14.2095L17 21L10 14.2095L11.225 13Z" fill="#ED1B24" />
                                                            </svg>

                                                        </span>
                                                    </button>
                                                    <div
                                                        id={`acc-content-${service.id}-${acc.id}`}
                                                        className="accordion-content"
                                                        style={{ maxHeight: isOpen ? '200px' : '0px' }}
                                                    >
                                                        <div className="accordion-content-inner text-sm md:text-base">
                                                            {acc.content}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Container>
        </section>
    );
};

export default ServicesCoreLogistics;
